<script setup lang="ts">
  import type { ProductFlexibleResource, ProductVariantSimpleResource, ProductVariantPickerResource } from '@storefront/resources';
  import { usePersistentFormContext, useHttpClient, useRoute } from '@othercommerce/foundation';
  import { computed, ref, type Ref, watch, nextTick, onMounted } from 'vue';

  interface SelectedItem {
    productId: number,
    variantId: number | null,
    isDropShipping: boolean,
    stockAvailable: number,
    stockOnOrder: boolean,
    quantity: number,
    isMedicinalProduct: boolean,
  }

  interface AttributeOption {
    label: string,
    value: number,
  }

  interface Attribute {
    label: string,
    options: AttributeOption[],
    value: number,
  }

  interface Props {
    selected: SelectedItem,
    product: ProductFlexibleResource,
    index: number,
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const http = useHttpClient();

  const { data } = usePersistentFormContext();

  const variant = ref() as Ref<ProductVariantPickerResource>;
  const loading = ref(true);
  const attributes = ref() as Ref<Attribute[]>;
  const selected = ref() as Ref<any>;
  const variants = ref([]) as Ref<ProductVariantSimpleResource[]>;

  const options = computed(() => props.product.flexibleItems.map((item) => {
    return {
      label: item.title,
      value: item.productId,
    };
  }));

  const item = computed(() => {
    return props.product.flexibleItems.find((item) => {
      return item.productId === props.selected.productId;
    })!;
  });

  function onSelected(variant: ProductVariantSimpleResource) {
    data.value.items[props.index].productId = variant.productId;
    data.value.items[props.index].variantId = variant.id;
  }

  function removeItem() {
    data.value.items.splice(props.index, 1);
  }

  function reloadVariantsForProduct(productId: number | undefined) {
    // Always get default variant for product when 'Wybierz produkt' select is used
    // That prevents bugs (holding data for old variant instead of new one) when user changes selected item to different one
    const selectedItem = props.product.flexibleItems.find((item) => item.productId === productId);

    let params = {
      product: selectedItem?.productId,
      variant: selectedItem?.variantId,
      allowed: item.value.variants.length > 0 ? item.value.variants : undefined,
    };

    http.dispatch('GET', route('web.product.picker.api', params)).then((response) => {
      variant.value = response.variant;
      attributes.value = response.attributes;
      selected.value = response.selected;
      variants.value = response.variants;

      nextTick(() => {
        loading.value = false;
      });
    });
  }

  function reloadVariantsForVariant(variantId: number | undefined) {
    // Get new variant only when SfVariantPickerAttributes or SfVariantPickerAttributes modifies variantId
    let params = {
      product: data.value.items[props.index].productId,
      variant: variantId,
      allowed: item.value.variants.length > 0 ? item.value.variants : undefined,
    };

    http.dispatch('GET', route('web.product.picker.api', params)).then((response) => {
      variant.value = response.variant;
      attributes.value = response.attributes;
      selected.value = response.selected;
      variants.value = response.variants;

      nextTick(() => {
        loading.value = false;
      });
    });
  }

  watch(() => data.value.items[props.index].productId, (productId) => {
    reloadVariantsForProduct(productId);
  });

  watch(() => data.value.items[props.index].variantId, (variantId) => {
    reloadVariantsForVariant(variantId);
  });

  watch(variant, (variant) => {
    if (variant) {
      data.value.items[props.index].productId = variant.productId;
      data.value.items[props.index].variantId = variant.id;
      data.value.items[props.index].stockAvailable = variant.stockAvailable;
      data.value.items[props.index].stockOnOrder = variant.stockOnOrder;
      data.value.items[props.index].isMedicinalProduct = variant.isMedicinalProduct;
    }
  }, {
    immediate: true,
  });

  onMounted(() => {
    if (data.value.items[props.index].productId) {
      reloadVariantsForProduct(data.value.items[props.index].productId);
    }
  });
</script>
<template>
  <div class="flexible-item">
    <div class="item">
      <div class="item-product">
        <SfFormSelect :label="$t('SHOP.PRODUCT.ACTION.CHOOSE_PRODUCT')" :floating="false" :name="`items[${index}][productId]`" :options="options" />
      </div>
      <template v-if="loading === false">
        <div class="item-variant" v-if="attributes.length > 0 || variants.length > 0 ">
          <SfVariantPickerAttributes v-if="attributes.length > 0" :variant :attributes :selected :floating-labels="false" @selected="onSelected" />
          <SfVariantPickerGrid v-else-if="variants.length > 0" show="variants" :variant :variants @selected="onSelected" />
        </div>
      </template>
      <div class="quantity-delete-wrapper">
        <div class="item-quantity">
          <SfFormNumber
            :label="$t('SHOP.COMMON.LABEL.QUANTITY')"
            :name="`items[${index}][quantity]`"
            :min="item.stockUnitBase"
            :max="item.isDropShipping ? undefined : variant?.stockAvailable"
            :step="item.stockUnitBase"
          />
        </div>
        <div class="item-actions">
          <SfButton transparent square danger @click="removeItem()" :aria-label="$t('SHOP.ARIA.ACTION.REMOVE_FROM_SET')">
            <SfIconTrash aria-hidden="true" />
          </SfButton>
        </div>
      </div>
    </div>
    <div v-if="item.isDropShipping" class="drop-shipping">
      <div class="icon">
        <SfIconQuestionCircle aria-hidden="true" />
      </div>
      <div class="message">
        {{ $t('SHOP.PRODUCT.MESSAGE.DIRECTLY_FROM_MANUFACTURER') }}
      </div>
    </div>
    <Transition name="fade">
      <div v-if="!item.isDropShipping && variant && Number(variant.stockAvailable) < Number(data.items[index].quantity)" class="unavailable">
        <div class="icon">
          <SfIconError aria-hidden="true" />
        </div>
        <span class="message">
          <template v-if="variant.stockAvailable < variant.stockUnitBase">
            {{ $t('SHOP.PRODUCT.MESSAGE.PRODUCT_NOT_AVAILABLE') }}
          </template>
          <template v-else>
            {{ $t('SHOP.PRODUCT.MESSAGE.PRODUCT_OUT_OF_STOCK') }}
          </template>
        </span>
      </div>
    </Transition>
  </div>
</template>
<style scoped lang="scss">
  @use '@storefront/styles/mixins/media';

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity var(--transition-primary-duration) var(--transition-primary-timing-function);
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .flexible-item {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    padding: 0.875rem 1rem;
    border-radius: var(--border-radius);
    background-color: var(--subtle-background);
    border: 1px solid var(--secondary-border);

    @include media.query(lg) {
      gap: 0.375rem;
      padding: 0.5rem;
    }
  }

  .drop-shipping, .unavailable {
    display: flex;
    flex-direction: row;
    align-self: flex-start;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.75rem;
    line-height: 1.5;
    margin-top: 0.25rem;
    padding: 0.25rem 0.75rem;
    border-radius: var(--border-radius);

    .icon {
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 0 0 auto;
      font-size: 0.875rem;
      height: 1.125rem;
      width: 0.875rem;
    }
  }

  .drop-shipping {
    color: var(--sf-status-color-info);
    background-color: var(--sf-status-background-info);
    border: 1px solid var(--sf-status-color-info);
  }

  .unavailable {
    color: var(--sf-status-color-danger);
    background-color: var(--sf-status-background-danger);
    border: 1px solid var(--sf-status-color-danger);
  }

  .item {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;

    .item-product {
      flex: 1 0 300px;
    }

    .item-variant {
      flex: 1 0 auto;
      max-width: 100%;

      :deep(.variant-attributes) {
        .variant-attributes__list {
          flex-direction: row;

          .control-wrapper {
            flex: 1 0 150px;
          }
        }
      }

      @include media.query(md) {
        width: 100%;
      }
    }

    .quantity-delete-wrapper {
      display: flex;
      align-items: flex-end;
      justify-self: flex-end;
      flex: 0 0 auto;
      gap: 0.75rem;
      margin-left: auto;

      @include media.query(lg) {
        justify-content: space-between;
        flex-basis: 100%;
      }
    }

    .item-quantity {
      width: 150px;
    }

    .item-price {
      padding: 1.25rem;

      .price {
        font-weight: var(--font-weight-bold);
      }

      .per-unit {
        color: var(--secondary-color);
        font-size: var(--font-size-sm);
      }
    }
  }
</style>
